import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  EscalationResponseType,
  EscalationRule,
  EscalationTimeConditionType,
  SERVICE_LEVEL_PRIORITY_ORDER,
} from '@provision/sidekick-utils';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  ESCALATION_RESPONSE_TYPE_OPTIONS,
  ESCALATION_TIME_CONDITION_OPTIONS,
  ESCALATION_URGENCY_DISPLAY_LABELS,
  ESCALATION_WEEKDAY_OPTIONS,
  formatEnumLabel,
} from './escalation-utils';

interface EscalationRuleModalProps {
  open: boolean;
  initialRule?: EscalationRule | null;
  order: number;
  urgencyLevel: string;
  onClose: () => void;
  onSave: (rule: EscalationRule) => void;
}

interface EscalationRuleFormValues {
  name: string;
  allServiceLevels: boolean;
  serviceLevels: string[];
  timeCondition: EscalationTimeConditionType;
  responseType: EscalationResponseType;
  durationMinutes: number;
  fixedTime: string;
  weekDay: number | '';
  customWeekDays: number[];
  customTimeFrom: string;
  customTimeTo: string;
}

const TIME_ESCALATION_RESPONSE_TYPES = [
  'by_fixed_time',
  'by_next_business_day',
  'by_tomorrow',
];

export const EscalationRuleModal = ({
  open,
  initialRule,
  order,
  urgencyLevel,
  onClose,
  onSave,
}: EscalationRuleModalProps) => {
  const defaultValues = useMemo(() => buildDefaultValues(initialRule), [initialRule]);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EscalationRuleFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  const allServiceLevels = useWatch({
    control,
    name: 'allServiceLevels',
    defaultValue: defaultValues.allServiceLevels,
  });
  const serviceLevels = useWatch({
    control,
    name: 'serviceLevels',
    defaultValue: defaultValues.serviceLevels,
  });
  const timeCondition = useWatch({
    control,
    name: 'timeCondition',
    defaultValue: defaultValues.timeCondition,
  });
  const responseType = useWatch({
    control,
    name: 'responseType',
    defaultValue: defaultValues.responseType,
  });
  const durationMinutes = useWatch({
    control,
    name: 'durationMinutes',
    defaultValue: defaultValues.durationMinutes,
  });
  const fixedTime = useWatch({
    control,
    name: 'fixedTime',
    defaultValue: defaultValues.fixedTime,
  });
  const weekDay = useWatch({
    control,
    name: 'weekDay',
    defaultValue: defaultValues.weekDay,
  });

  const urgencyLabel = ESCALATION_URGENCY_DISPLAY_LABELS[urgencyLevel] ?? urgencyLevel;

  const rulePreview = buildRulePreview({
    urgencyLabel,
    allServiceLevels,
    serviceLevels,
    timeCondition,
    responseType,
    durationMinutes,
    fixedTime,
    weekDay,
  });

  const onSubmit = (values: EscalationRuleFormValues) => {
    onSave(toEscalationRule(values, initialRule?.id ?? crypto.randomUUID(), order));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="overline">{initialRule ? 'Edit Rule' : 'Add Rule'}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack spacing={1.5}>
          {/* Live Preview */}
          <Box
            sx={{
              backgroundColor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                mb: 0.5,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.6875rem',
              }}
            >
              Rule Preview
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
              {rulePreview}
            </Typography>
          </Box>

          {/* Rule Name */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Rule name"
                placeholder="e.g., Signature clients after hours"
              />
            )}
          />

          <Controller
            name="allServiceLevels"
            control={control}
            render={({ field }) => (
              <TextField
                select
                size="small"
                label="Service level selector"
                value={field.value ? 'all' : 'selected'}
                onChange={(event) => field.onChange(event.target.value === 'all')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="selected">Selected service levels</MenuItem>
              </TextField>
            )}
          />

          {!allServiceLevels ? (
            <Controller
              name="serviceLevels"
              control={control}
              rules={{
                validate: (value) => {
                  if (!allServiceLevels && value.length === 0) {
                    return 'At least one service level is required';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={SERVICE_LEVEL_PRIORITY_ORDER}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Service levels"
                      error={!!errors.serviceLevels}
                      helperText={errors.serviceLevels?.message}
                    />
                  )}
                />
              )}
            />
          ) : null}

          <Controller
            name="timeCondition"
            control={control}
            render={({ field }) => (
              <TextField
                select
                size="small"
                label="Escalates when"
                value={field.value}
                onChange={(event) =>
                  field.onChange(event.target.value as EscalationTimeConditionType)
                }
              >
                {ESCALATION_TIME_CONDITION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {timeCondition === 'custom' ? (
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Days
                </Typography>
                <Controller
                  name="customWeekDays"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (timeCondition === 'custom' && value.length === 0) {
                        return 'At least one day must be selected';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      value={field.value}
                      onChange={(_, newDays: number[]) => {
                        if (newDays.length > 0) {
                          field.onChange(newDays);
                        }
                      }}
                      size="small"
                    >
                      {ESCALATION_WEEKDAY_OPTIONS.map((option) => (
                        <ToggleButton
                          key={option.value}
                          value={option.value}
                          sx={{ px: 1.5, minWidth: 40 }}
                        >
                          {option.label.slice(0, 3)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                />
                {errors.customWeekDays ? (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {errors.customWeekDays.message}
                  </Typography>
                ) : null}
              </Box>

              <Stack direction="row" spacing={1}>
                <Controller
                  name="customTimeFrom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="time"
                      label="From"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />

                <Controller
                  name="customTimeTo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="time"
                      label="To"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          ) : null}

          <Controller
            name="responseType"
            control={control}
            render={({ field }) => (
              <TextField
                select
                size="small"
                label="Response"
                value={field.value}
                onChange={(event) =>
                  field.onChange(event.target.value as EscalationResponseType)
                }
              >
                {ESCALATION_RESPONSE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {responseType === 'fixed_duration' ? (
            <Controller
              name="durationMinutes"
              control={control}
              rules={{
                validate: (value) => {
                  if (responseType !== 'fixed_duration') {
                    return true;
                  }
                  if (value <= 0) {
                    return 'Must be greater than 0';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  size="small"
                  type="number"
                  label="Duration (minutes)"
                  value={field.value}
                  onChange={(event) => field.onChange(Number(event.target.value || 0))}
                  error={!!errors.durationMinutes}
                  helperText={errors.durationMinutes?.message}
                />
              )}
            />
          ) : null}

          {TIME_ESCALATION_RESPONSE_TYPES.includes(responseType) ? (
            <Controller
              name="fixedTime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  type="time"
                  label="Time"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          ) : null}

          {responseType === 'by_fixed_time' ? (
            <Controller
              name="weekDay"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  size="small"
                  label="Weekday (optional)"
                  value={field.value}
                  onChange={(event) => {
                    const { value } = event.target;
                    field.onChange(value === '' ? '' : Number(value));
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {ESCALATION_WEEKDAY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)}>
          {initialRule ? 'Save changes' : 'Add rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function getInitialServiceLevels(initialRule?: EscalationRule | null): string[] {
  if (initialRule?.serviceLevels === 'all') {
    return [];
  }

  if (Array.isArray(initialRule?.serviceLevels)) {
    return initialRule.serviceLevels;
  }

  return [];
}

function getInitialWeekDay(initialRule?: EscalationRule | null): number | '' {
  const weekDay = initialRule?.response.weekDay;
  if (initialRule?.response.type !== 'by_fixed_time' || typeof weekDay !== 'number') {
    return '';
  }

  return weekDay;
}

function buildDefaultValues(initialRule?: EscalationRule | null): EscalationRuleFormValues {
  const schedule = initialRule?.timeCondition.customSchedule ?? [];
  const weekDays =
    schedule.length > 0 ? [...new Set(schedule.map((w) => w.weekDay))] : [1, 2, 3, 4, 5];

  return {
    name: initialRule?.name ?? '',
    allServiceLevels: initialRule?.serviceLevels === 'all',
    serviceLevels: getInitialServiceLevels(initialRule),
    timeCondition: initialRule?.timeCondition.type ?? 'always',
    responseType: initialRule?.response.type ?? 'fixed_duration',
    durationMinutes: initialRule?.response.durationMinutes ?? 60,
    fixedTime: initialRule?.response.time ?? '17:00',
    weekDay: getInitialWeekDay(initialRule),
    customWeekDays: weekDays,
    customTimeFrom: schedule[0]?.timeFrom ?? '09:00',
    customTimeTo: schedule[0]?.timeTo ?? '17:00',
  };
}

function toEscalationRule(
  values: EscalationRuleFormValues,
  id: string,
  order: number,
): EscalationRule {
  return {
    id,
    order,
    name: values.name || undefined,
    serviceLevels: values.allServiceLevels ? 'all' : values.serviceLevels,
    timeCondition: {
      type: values.timeCondition,
      customSchedule:
        values.timeCondition === 'custom'
          ? values.customWeekDays.map((weekDay) => ({
              weekDay,
              timeFrom: values.customTimeFrom,
              timeTo: values.customTimeTo,
            }))
          : undefined,
    },
    response: {
      type: values.responseType,
      durationMinutes:
        values.responseType === 'fixed_duration' ? values.durationMinutes : undefined,
      time: TIME_ESCALATION_RESPONSE_TYPES.includes(values.responseType)
        ? values.fixedTime
        : undefined,
      weekDay:
        values.responseType === 'by_fixed_time' && values.weekDay !== ''
          ? values.weekDay
          : undefined,
    },
  };
}

interface PreviewParams {
  urgencyLabel: string;
  allServiceLevels: boolean;
  serviceLevels: string[];
  timeCondition: EscalationTimeConditionType;
  responseType: EscalationResponseType;
  durationMinutes: number;
  fixedTime: string;
  weekDay: number | '';
}

function buildRulePreview(params: PreviewParams): string {
  const {
    urgencyLabel,
    allServiceLevels,
    serviceLevels = [],
    timeCondition,
    responseType,
    durationMinutes,
    fixedTime,
    weekDay,
  } = params;

  let serviceLevelText;

  if (allServiceLevels || serviceLevels.length === 0) {
    serviceLevelText = 'any client';
  } else if (serviceLevels.length === 1) {
    serviceLevelText = `${serviceLevels[0]} clients`;
  } else {
    const allButLast = serviceLevels.slice(0, -1).join(', ');
    const last = serviceLevels[serviceLevels.length - 1];
    serviceLevelText = `${allButLast} or ${last} clients`;
  }

  const timeText = formatEnumLabel(timeCondition);

  let responseText: string;

  switch (responseType) {
    case 'fixed_duration':
      responseText =
        durationMinutes >= 60 && durationMinutes % 60 === 0
          ? `within ${durationMinutes / 60} hour${durationMinutes / 60 !== 1 ? 's' : ''}`
          : `within ${durationMinutes} minutes`;
      break;
    case 'by_end_of_day':
      responseText = 'by end of day';
      break;
    case 'by_fixed_time': {
      const weekdayLabel = ESCALATION_WEEKDAY_OPTIONS.find(
        (option) => option.value === weekDay,
      )?.label;
      responseText = weekdayLabel ? `by ${fixedTime} on ${weekdayLabel}` : `by ${fixedTime}`;
      break;
    }
    case 'by_next_business_day':
      responseText = `by ${fixedTime} next business day`;
      break;
    case 'by_tomorrow':
      responseText = `by ${fixedTime} tomorrow`;
      break;
    case 'best_effort':
      responseText = 'as soon as we can (best effort)';
      break;
    default:
      responseText = 'best effort';
  }

  return `If escalated as ${urgencyLabel} by ${serviceLevelText} ${timeText}, respond ${responseText}.`;
}
